/**
 * Les mots des champions à la fin d'une activité : une banque par champion, rangée par niveau de la
 * séance (intro, boost, momentum, performance) et, pour les rares, par déclencheur (la première
 * activité, l'enchaînement rapide).
 *
 * Déplacé tel quel de `student/activity/execute/CelebrationConstants.js` : seuls des types sont
 * ajoutés. L'ancien écran de fin et le nouveau lisent les mêmes.
 */
export type CelebrationMessage = {
    title: string
    subtitle: string
    level: string
    trigger?: string
}

export const samCelebrations: CelebrationMessage[] = [
    // 🎯 INTRO – 1ère activité de la session
    {
        title: "Top départ",
        subtitle: "Le premier clic, et c’est parti pour briller.",
        level: "intro",
    },
    {
        title: "C’est lancé !",
        subtitle: "Une session qui commence fort.",
        level: "intro",
    },
    {
        title: "Premier envoi",
        subtitle: "T’as appuyé au bon moment, comme un pro du timing.",
        level: "intro",
    },
    {
        title: "Le flow démarre",
        subtitle: "On sent que tu vas enchaîner tranquille.",
        level: "intro",
    },
    {
        title: "Tout commence ici",
        subtitle: "Tu viens de poser ta première pierre.",
        level: "intro",
    },
    {
        title: "Démarrage stylé",
        subtitle: "Un début propre, net, sans bavure.",
        level: "intro",
    },
    // ⚡ BOOST – 2e à 3e activité
    {
        title: "T’es lancé·e",
        subtitle: "Ton rythme est fluide comme une bonne rime.",
        level: "boost",
    },
    {
        title: "Tu déroules",
        subtitle: "Une activité après l’autre, comme si de rien n’était.",
        level: "boost",
    },
    {
        title: "Le style continue",
        subtitle: "T’écris ton effort avec élégance.",
        level: "boost",
    },
    {
        title: "Bonne vibe",
        subtitle: "T’as trouvé ton rythme, et ça se voit.",
        level: "boost",
    },
    {
        title: "Ça glisse",
        subtitle: "Tu passes d’une activité à l’autre avec classe.",
        level: "boost",
    },
    {
        title: "Toujours fluide",
        subtitle: "Ton enchaînement fait plaisir à voir.",
        level: "boost",
    },
    {
        title: "On garde le tempo",
        subtitle: "Tu restes dans la danse du travail.",
        level: "boost",
    },
    // 🔥 MOMENTUM – 4e à 5e activité
    {
        title: "Ça devient sérieux",
        subtitle: "T’enchaînes comme un boss discret.",
        level: "momentum",
    },
    {
        title: "Belle régularité",
        subtitle: "Ton flow tient la route, sans effort.",
        level: "momentum",
    },
    {
        title: "Tu fais ton show",
        subtitle: "Sans bruit, mais avec beaucoup de style.",
        level: "momentum",
    },

    // 🏁 PERFORMANCE – 6e activité et plus
    {
        title: "Rythme de feu",
        subtitle: "T’enchaînes avec maîtrise, sans flamber.",
        level: "performance",
    },
    {
        title: "Session solide",
        subtitle: "T’as gardé le cap du début à la fin.",
        level: "performance",
    },
    {
        title: "Ligne après ligne",
        subtitle: "Tu construis ton effort sans relâche.",
        level: "performance",
    },
    {
        title: "Champion du tempo",
        subtitle: "Tu bosses comme un vrai maestro.",
        level: "performance",
    },
    // 🌟 RARES – Déclencheurs spéciaux
    {
        title: "Première vibe",
        subtitle: "Ta toute première activité ! Bienvenue dans le flow ✨",
        level: "rare",
        trigger: "first_activity",
    },
    {
        title: "C’est le début du style",
        subtitle: "Ta toute première session commence ici 👊",
        level: "rare",
        trigger: "first_activity",
    },
    {
        title: "Combo rapide !",
        subtitle: "Trois activités en un éclair, t’es en feu 🔥",
        level: "rare",
        trigger: "fast_combo",
    },
    {
        title: "T’envoies fort",
        subtitle: "T’as même pas pris le temps de respirer 😎",
        level: "rare",
        trigger: "fast_combo",
    },
]

export const tinaCelebrations: CelebrationMessage[] = [
    // 🎯 INTRO – 1ère activité de la session
    {
        title: "C’est parti !",
        subtitle: "On forme déjà une super équipe !",
        level: "intro",
    },
    {
        title: "Première mission",
        subtitle: "Tu lances le travail avec énergie ✨",
        level: "intro",
    },
    {
        title: "Un bon départ",
        subtitle: "Tout commence par un premier pas... ensemble !",
        level: "intro",
    },
    {
        title: "Ça démarre bien",
        subtitle: "On est déjà dans le bon tempo 💪",
        level: "intro",
    },
    {
        title: "L’équipe se met en route",
        subtitle: "Bravo pour cette première activité !",
        level: "intro",
    },
    {
        title: "Bien joué !",
        subtitle: "T’as lancé la dynamique, et j’adore ça.",
        level: "intro",
    },
    // ⚡ BOOST – 2e à 3e activité
    {
        title: "On enchaîne !",
        subtitle: "Tu gardes le rythme, c’est top.",
        level: "boost",
    },
    {
        title: "Travail d’équipe",
        subtitle: "T’es au rendez-vous, comme toujours 💛",
        level: "boost",
    },
    {
        title: "On avance ensemble",
        subtitle: "Et ça fait plaisir à voir !",
        level: "boost",
    },
    {
        title: "Tu restes motivé·e",
        subtitle: "J’suis là avec toi à chaque étape !",
        level: "boost",
    },
    {
        title: "C’est fluide",
        subtitle: "Tu continues avec énergie et sérieux.",
        level: "boost",
    },
    {
        title: "T’assures",
        subtitle: "Chaque activité compte, et tu le sais.",
        level: "boost",
    },
    {
        title: "Super rythme",
        subtitle: "On sent que tu donnes le meilleur !",
        level: "boost",
    },
    // 🔥 MOMENTUM – 4e à 5e activité
    {
        title: "Tu tiens le cap",
        subtitle: "On fait une belle équipe, c’est clair !",
        level: "momentum",
    },
    {
        title: "Trop bien !",
        subtitle: "Tu continues sans relâche, je suis fière de toi.",
        level: "momentum",
    },
    {
        title: "Ça enchaîne",
        subtitle: "T’es bien dans ta session, bravo !",
        level: "momentum",
    },
    {
        title: "Toujours là",
        subtitle: "Une vraie partenaire de travail 🔥",
        level: "momentum",
    },
    // 🏁 PERFORMANCE – 6e activité et plus
    {
        title: "Travail intense",
        subtitle: "T’es à fond, c’est super impressionnant !",
        level: "performance",
    },
    {
        title: "Sacré duo",
        subtitle: "On va loin ensemble 💪",
        level: "performance",
    },
    {
        title: "Belle session",
        subtitle: "Tu montres que t’es solide du début à la fin.",
        level: "performance",
    },
    {
        title: "On ne t’arrête plus",
        subtitle: "T’as pris le rythme et tu le tiens !",
        level: "performance",
    },
    // 🌟 RARES – Déclencheurs spéciaux
    {
        title: "Bienvenue !",
        subtitle: "C’est ta toute première activité 🥳 On commence fort !",
        level: "rare",
        trigger: "first_activity",
    },
    {
        title: "On débute ensemble",
        subtitle: "Je suis trop contente d’être là avec toi ✨",
        level: "rare",
        trigger: "first_activity",
    },
    {
        title: "Combo d’enfer !",
        subtitle: "Plusieurs activités en un rien de temps 💥",
        level: "rare",
        trigger: "fast_combo",
    },
    {
        title: "C’est du travail d’équipe",
        subtitle: "T’as enchaîné super vite, bravo !",
        level: "rare",
        trigger: "fast_combo",
    },
]

export const zoeCelebrations: CelebrationMessage[] = [
    // 🎯 INTRO – 1ère activité de la session
    {
        title: "Un bon départ",
        subtitle: "Tu viens de lancer ta session avec méthode.",
        level: "intro",
    },
    {
        title: "C’est parti",
        subtitle: "Premier pas posé, l’avancée commence.",
        level: "intro",
    },
    {
        title: "C'est carré'",
        subtitle: "Tu viens d’ajouter une brique à ton travail.",
        level: "intro",
    },
    {
        title: "C’est structuré",
        subtitle: "On part sur de bonnes bases.",
        level: "intro",
    },
    {
        title: "Première étape",
        subtitle: "Tu viens d’activer ton plan de route.",
        level: "intro",
    },
    {
        title: "À toi de jouer",
        subtitle: "Et ça commence bien, dans le calme.",
        level: "intro",
    },
    //⚡ BOOST – 2e à 3e activité
    {
        title: "Ça avance",
        subtitle: "Tu continues avec régularité.",
        level: "boost",
    },
    {
        title: "Organisation en marche",
        subtitle: "Tu suis ton rythme, et ça fonctionne.",
        level: "boost",
    },
    {
        title: "Constance au rendez-vous",
        subtitle: "Tu restes sur ta lancée, c’est top.",
        level: "boost",
    },
    {
        title: "Pas à pas",
        subtitle: "Tu construis ton avancée avec sérieux.",
        level: "boost",
    },
    {
        title: "Bonne dynamique",
        subtitle: "Chaque nouvelle activité compte.",
        level: "boost",
    },
    {
        title: "Tu suis ton plan",
        subtitle: "Et c’est exactement ce qu’il faut.",
        level: "boost",
    },
    {
        title: "Rythme posé",
        subtitle: "Tu avances sans précipitation, bien vu.",
        level: "boost",
    },
    // 🔥 MOMENTUM – 4e à 5e activité
    {
        title: "Avancée solide",
        subtitle: "Tu restes constant·e dans l’effort.",
        level: "momentum",
    },
    {
        title: "Tu restes concentré·e",
        subtitle: "Ta session progresse tranquillement.",
        level: "momentum",
    },
    {
        title: "Belle continuité",
        subtitle: "Encore une activité ajoutée à ton parcours.",
        level: "momentum",
    },
    {
        title: "Session structurée",
        subtitle: "Ton temps de travail est bien mené.",
        level: "momentum",
    },
    // 🏁 PERFORMANCE – 6e activité et plus
    {
        title: "Longue session",
        subtitle: "Tu tiens un bon rythme depuis le début.",
        level: "performance",
    },
    {
        title: "Organisation en action",
        subtitle: "Ta séance avance à bon rythme.",
        level: "performance",
    },
    {
        title: "Rigueur continue",
        subtitle: "Tu restes régulier·ère dans tes envois.",
        level: "performance",
    },
    {
        title: "Session bien remplie",
        subtitle: "Tu avances avec calme et méthode.",
        level: "performance",
    },
    // 🌟 RARES – Déclencheurs spéciaux
    {
        title: "Bienvenue à toi !",
        subtitle: "Tu viens de lancer ta toute première activité 🧠",
        level: "rare",
        trigger: "first_activity",
    },
    {
        title: "Départ organisé",
        subtitle: "Ta première activité est enregistrée, c’est lancé !",
        level: "rare",
        trigger: "first_activity",
    },
    {
        title: "Combo structuré",
        subtitle: "Trois activités enchaînées sans perdre le cap 🗂️",
        level: "rare",
        trigger: "fast_combo",
    },
    {
        title: "Enchaînement régulier",
        subtitle: "T’as suivi le plan à toute vitesse 💡",
        level: "rare",
        trigger: "fast_combo",
    },
]

export const abouCelebrations: CelebrationMessage[] = [
    // 🎯 INTRO – 1ère activité de la session
    {
        title: "L’esprit s’éveille",
        subtitle: "Tu entres dans le travail comme on entre dans un temple : en silence, mais avec puissance.",
        level: "intro",
    },
    {
        title: "Premier signal",
        subtitle: "Une seule action suffit à activer l’attention.",
        level: "intro",
    },
    {
        title: "Le calme s’installe",
        subtitle: "Ta session débute comme un souffle maîtrisé.",
        level: "intro",
    },
    {
        title: "Connexion en cours",
        subtitle: "Ton esprit se met doucement en route.",
        level: "intro",
    },
    {
        title: "Une pensée posée",
        subtitle: "Premier pas dans une session sans bruit, mais pas sans intensité.",
        level: "intro",
    },
    {
        title: "Le cercle est ouvert",
        subtitle: "Tu viens de tracer la première ligne de ta concentration.",
        level: "intro",
    },
    // ⚡ BOOST – 2e à 3e activité
    {
        title: "Tu restes centré·e",
        subtitle: "Comme un laser qui reste sur sa cible.",
        level: "boost",
    },
    {
        title: "Souffle régulier",
        subtitle: "Chaque envoi est une respiration maîtrisée.",
        level: "boost",
    },
    {
        title: "Stabilité",
        subtitle: "Tu avances sans être distrait·e, et c’est rare.",
        level: "boost",
    },
    {
        title: "Pensée continue",
        subtitle: "Tu gardes le fil, comme un·e maître du focus.",
        level: "boost",
    },
    {
        title: "Une bulle solide",
        subtitle: "Ton espace mental reste fermé aux distractions.",
        level: "boost",
    },
    {
        title: "Alignement parfait",
        subtitle: "Esprit, objectif, action : tout avance ensemble.",
        level: "boost",
    },
    // 🔥 MOMENTUM – 4e à 5e activité
    {
        title: "Tu entres dans la zone",
        subtitle: "Le temps passe, mais ta concentration ne faiblit pas.",
        level: "momentum",
    },
    {
        title: "Session profonde",
        subtitle: "Tu t’enfonces dans ton travail comme dans un tunnel de clarté.",
        level: "momentum",
    },
    {
        title: "Rien ne t’échappe",
        subtitle: "Tu suis ta trajectoire avec une précision remarquable.",
        level: "momentum",
    },
    {
        title: "Ténacité",
        subtitle: "Tout avance à la bonne fréquence.",
        level: "momentum",
    },
    // 🏁 PERFORMANCE – 6e activité et plus
    {
        title: "Concentration d’élite",
        subtitle: "Peu arrivent à tenir aussi longtemps sans relâcher l’effort.",
        level: "performance",
    },
    {
        title: "Mental de moine",
        subtitle: "Tu poursuis ton chemin, sans bruit, sans pause.",
        level: "performance",
    },
    {
        title: "Rythme intérieur",
        subtitle: "C’est ton tempo, et rien ne l’interrompt.",
        level: "performance",
    },
    {
        title: "Session stable et claire",
        subtitle: "Tu crées ton avancée, pensée après pensée.",
        level: "performance",
    },
    // 🌟 RARES – Déclencheurs spéciaux
    {
        title: "Premier souffle",
        subtitle: "C’est ta toute première activité. L’esprit vient de s’ouvrir.",
        level: "rare",
        trigger: "first_activity",
    },
    {
        title: "Ouverture du cycle",
        subtitle: "Une session commence, et une discipline naît.",
        level: "rare",
        trigger: "first_activity",
    },
    {
        title: "Éveil fulgurant",
        subtitle: "Deux activités enchaînées avec une vitesse rare.",
        level: "rare",
        trigger: "fast_combo",
    },
    {
        title: "Concentration absolue",
        subtitle: "Tu n’as pas cligné des yeux. Presque.",
        level: "rare",
        trigger: "fast_combo",
    },
]
